// src/pages/UsersPage.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Roles } from '../constants/permissions.ts';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api.ts';
import { Search, User, Filter, CheckCircle, XCircle, Calendar } from 'lucide-react';

interface UserDto {
  id: number;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  roleAssignedAt?: string;
}

interface UserSearchParams {
  name?: string;
  email?: string;
  role?: string;
}

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserDto[]>([]);
  const [searchParams, setSearchParams] = useState<UserSearchParams>({});
  const [isLoading, setIsLoading] = useState(true);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    userId?: number;
    newRole?: string;
    userName?: string;
  }>({ isOpen: false });

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const users = await api.get<UserDto[]>('users', searchParams);
      setUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = async (userId: number, newRole: string, userName: string) => {
    setConfirmDialog({
      isOpen: true,
      userId,
      newRole,
      userName
    });
  };

  useEffect(() => {
    fetchUsers();
  }, [searchParams]);

  const confirmRoleChange = async () => {
    if (!confirmDialog.userId || !confirmDialog.newRole) return;
  
    try {
      await api.put(`users/${confirmDialog.userId}/role`, {
        role: confirmDialog.newRole
      });
    } catch (error) {
      console.error('Error updating role:', error);
    } finally {
      setConfirmDialog({ isOpen: false });
      await fetchUsers();
    }
  };

  // Renders a mobile card for each user
  const renderMobileUserCard = (user: UserDto) => (
    <div key={user.id} className="bg-white rounded-lg shadow p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-500 font-semibold mr-2">
            {user.name.charAt(0)}
          </div>
          <div className="truncate">
            <div className="font-medium text-sm">{user.email}</div>
            <div className="text-xs text-gray-500">{user.name}</div>
          </div>
        </div>
        <div className={`w-2 h-2 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
      </div>
      
      <div className="mb-2">
        <select
          className="w-full px-3 py-2 border rounded-md text-sm"
          value={user.role}
          onChange={(e) => handleRoleChange(user.id, e.target.value, user.name)}
        >
          <option value={Roles.NONE}>{t('usersPage.search.role.none')}</option>
          <option value={Roles.ADMINISTRATOR}>{t('usersPage.search.role.administrator')}</option>
          <option value={Roles.BACK_OFFICE}>{t('usersPage.search.role.backOffice')}</option>
          <option value={Roles.FIELD_AGENT}>{t('usersPage.search.role.fieldAgent')}</option>
        </select>
      </div>
      
      <div className="flex justify-between text-xs text-gray-500 mt-2">
        <div className="flex items-center">
          <Calendar size={12} className="mr-1" />
          {new Date(user.createdAt).toLocaleDateString()}
        </div>
        {user.isActive ? (
          <div className="flex items-center text-green-600">
            <CheckCircle size={12} className="mr-1" />
            {t('usersPage.table.status.active')}
          </div>
        ) : (
          <div className="flex items-center text-red-600">
            <XCircle size={12} className="mr-1" />
            {t('usersPage.table.status.inactive')}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6">
      {/* Page Title */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl sm:text-2xl font-bold">{t('navigation.userManagement')}</h1>
        <button 
          onClick={() => setFiltersVisible(!filtersVisible)}
          className="flex items-center text-sm px-3 py-1 border rounded-md"
        >
          <Filter size={16} className="mr-1" />
          <span className="hidden sm:inline">Filters</span>
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className={`mb-6 bg-white rounded-lg shadow p-4 ${filtersVisible ? 'block' : 'hidden sm:block'}`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('usersPage.search.name')}
              className="pl-10 pr-3 py-2 w-full border rounded-md text-sm"
              onChange={(e) => setSearchParams(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('usersPage.search.email')}
              className="pl-10 pr-3 py-2 w-full border rounded-md text-sm"
              onChange={(e) => setSearchParams(prev => ({ ...prev, email: e.target.value }))}
            />
          </div>
          <select
            className="px-3 py-2 border rounded-md text-sm"
            onChange={(e) => setSearchParams(prev => ({ ...prev, role: e.target.value }))}
          >
            <option value="">{t('usersPage.search.role.label')}</option>
            <option value={Roles.NONE}>{t('usersPage.search.role.none')}</option>
            <option value={Roles.ADMINISTRATOR}>{t('usersPage.search.role.administrator')}</option>
            <option value={Roles.BACK_OFFICE}>{t('usersPage.search.role.backOffice')}</option>
            <option value={Roles.FIELD_AGENT}>{t('usersPage.search.role.fieldAgent')}</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      )}

      {/* Mobile View - Only shown on small screens */}
      <div className="block sm:hidden">
        {!isLoading && users.map(renderMobileUserCard)}
        {!isLoading && users.length === 0 && (
          <div className="bg-white rounded-lg p-8 text-center text-gray-500">
            No users found matching your search criteria
          </div>
        )}
      </div>

      {/* Desktop View - Table format */}
      <div className="hidden sm:block">
        {!isLoading && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('usersPage.table.headers.name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('usersPage.table.headers.email')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('usersPage.table.headers.role')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('usersPage.table.headers.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('usersPage.table.headers.joined')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('usersPage.table.headers.lastRoleChange')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map(user => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-500 font-semibold mr-2">
                            {user.name.charAt(0)}
                          </div>
                          <span>{user.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          className="px-2 py-1 border rounded-md text-sm"
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value, user.name)}
                        >
                          <option value={Roles.NONE}>{t('usersPage.search.role.none')}</option>
                          <option value={Roles.ADMINISTRATOR}>{t('usersPage.search.role.administrator')}</option>
                          <option value={Roles.BACK_OFFICE}>{t('usersPage.search.role.backOffice')}</option>
                          <option value={Roles.FIELD_AGENT}>{t('usersPage.search.role.fieldAgent')}</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.isActive ? t('usersPage.table.status.active') : t('usersPage.table.status.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.roleAssignedAt ? new Date(user.roleAssignedAt).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.length === 0 && (
              <div className="py-8 text-center text-gray-500">
                No users found matching your search criteria
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">{t('usersPage.confirmDialog.title')}</h3>
            <p className="text-gray-600 mb-6">
              {t('usersPage.confirmDialog.message', {
                userName: confirmDialog.userName,
                newRole: confirmDialog.newRole
              })}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 border rounded-md text-sm"
                onClick={() => setConfirmDialog({ isOpen: false })}
              >
                {t('usersPage.confirmDialog.cancel')}
              </button>
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm"
                onClick={confirmRoleChange}
              >
                {t('usersPage.confirmDialog.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;